import React from 'react';

import { ChoiceFormField, FormField, MergeDisplayFormField, RichTextFormField } from '~forms/types';
import { resolveTokens } from '~forms/lib/resolvedFieldValue';
import { RichTextView } from './rich-text';
import SignatureField from './SignatureField';

interface ComponentProps {
  field: FormField;
  resolved: Record<string, string>;
  /** Current response value for editable fields (keyed by field.id in parent state) */
  value: string;
  onChange: (fieldId: string, newValue: string) => void;
  error?: string;
}

/**
 * Renders a single form field in fill mode.
 * - Display-only types (section_divider, rich_text, merge_field, read_only fields): show resolved content.
 * - Editable types: render an interactive input bound to `value`/`onChange`.
 * - signature: capture control (SignatureField), bound to `value`/`onChange` like any other editable type.
 */
const FillFieldRenderer: React.FC<ComponentProps> = ({ field, resolved, value, onChange, error }) => {
  // ── Section divider ────────────────────────────────────────────────────────
  if (field.type === 'section_divider') {
    const heading = resolveTokens(field.label || 'Section', resolved);
    return (
      <h5 className="form-fill-section" aria-label={heading}>
        {heading}
      </h5>
    );
  }

  // ── Rich text ──────────────────────────────────────────────────────────────
  if (field.type === 'rich_text') {
    const rt = field as RichTextFormField;
    // Fill mode shows the live form, not a preview: substitute merge tokens with the resolved client
    // values before rendering, so they read as text (not blue "preview" chips). Registry is empty
    // because no {{merge:}} sentinels remain after resolution.
    return (
      <div className="form-fill-rich-text">
        <RichTextView html={resolveTokens(rt.content, resolved)} registry={[]} />
      </div>
    );
  }

  // ── Merge display field ────────────────────────────────────────────────────
  if (field.type === 'merge_field') {
    const mf = field as MergeDisplayFormField;
    const resolvedValue = resolved[mf.merge_token] ?? '';
    const labelText = resolveTokens(field.label || '', resolved);
    return (
      <div className="form-fill-field">
        {labelText && (
          <label className="form-fill-label">
            {labelText}
          </label>
        )}
        <div className="form-preview-static">{resolvedValue || <span className="text-muted">—</span>}</div>
      </div>
    );
  }

  // ── Read-only field ────────────────────────────────────────────────────────
  if (field.read_only) {
    const resolvedDefault = resolveTokens(field.default_value ?? '', resolved);
    const labelText = resolveTokens(field.label || '', resolved);
    return (
      <div className="form-fill-field">
        {labelText && <label className="form-fill-label">{labelText}</label>}
        {field.help_text && (
          <div className="form-preview-help">{resolveTokens(field.help_text, resolved)}</div>
        )}
        <div className="form-preview-static">{resolvedDefault || <span className="text-muted">—</span>}</div>
      </div>
    );
  }

  // ── Signature (capture control) ────────────────────────────────────────────
  if (field.type === 'signature') {
    const labelText = resolveTokens(field.label || '', resolved);
    const isRequired = field.required && !field.read_only;
    // The label is only rendered when the RESOLVED text is non-empty (a label that is nothing but an
    // unresolved merge token resolves to ''). `labelId` is therefore handed to SignatureField only in
    // that case, so its aria-labelledby can never point at an id that is absent from the document.
    const signatureLabelId = `fill-field-signature-${field.id}-label`;
    return (
      <div className="form-fill-field">
        {labelText && (
          <label className="form-fill-label" id={signatureLabelId}>
            {labelText}
            {isRequired && (
              <span className="text-danger ms-1" aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}
        {field.help_text && (
          <div className="form-preview-help">{resolveTokens(field.help_text, resolved)}</div>
        )}
        <SignatureField
          field={field}
          value={value}
          onChange={onChange}
          error={error}
          labelId={labelText ? signatureLabelId : undefined}
        />
      </div>
    );
  }

  // ── Editable fields ────────────────────────────────────────────────────────
  const isRequired = field.required && !field.read_only;
  const inputId = `fill-field-${field.id}`;
  const labelText = resolveTokens(field.label || '', resolved);

  const renderInput = () => {
    switch (field.type) {
      case 'single_line_text':
        return (
          <input
            id={inputId}
            className={`form-fill-input${error ? ' form-fill-input--error' : ''}`}
            type="text"
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            aria-label={resolveTokens(field.label || '', resolved)}
            aria-required={isRequired}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : undefined}
          />
        );

      case 'paragraph_text':
        return (
          <textarea
            id={inputId}
            className={`form-fill-input form-fill-input--multiline${error ? ' form-fill-input--error' : ''}`}
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            aria-label={resolveTokens(field.label || '', resolved)}
            aria-required={isRequired}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : undefined}
          />
        );

      case 'date':
        return (
          <input
            id={inputId}
            className={`form-fill-input${error ? ' form-fill-input--error' : ''}`}
            type="date"
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            aria-label={resolveTokens(field.label || '', resolved)}
            aria-required={isRequired}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : undefined}
          />
        );

      case 'checkbox':
        // Title renders in the shared label block above; the help text renders inline to the RIGHT of
        // the checkbox as its caption (the shared help block is skipped for checkboxes — see below).
        return (
          <div className="form-preview-checkbox">
            <input
              id={inputId}
              type="checkbox"
              checked={value === 'true'}
              onChange={(e) => onChange(field.id, e.target.checked ? 'true' : 'false')}
              aria-label={resolveTokens(field.label || 'Checkbox', resolved)}
              aria-required={isRequired}
              aria-invalid={!!error}
              aria-describedby={error ? `${inputId}-error` : undefined}
            />
            {field.help_text && (
              <label htmlFor={inputId} className="form-fill-checkbox-help">
                {resolveTokens(field.help_text, resolved)}
              </label>
            )}
          </div>
        );

      case 'dropdown': {
        const choiceField = field as ChoiceFormField;
        const opts = choiceField.options ?? [];
        return (
          <select
            id={inputId}
            className={`form-fill-input${error ? ' form-fill-input--error' : ''}`}
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            aria-label={resolveTokens(field.label || '', resolved)}
            aria-required={isRequired}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : undefined}
          >
            <option value="">— Select —</option>
            {opts.map((opt) => (
              <option key={opt.id} value={opt.value || opt.label}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      }

      case 'multiple_choice': {
        const choiceField = field as ChoiceFormField;
        const opts = choiceField.options ?? [];
        return (
          <div
            className="form-preview-choice"
            role="radiogroup"
            // A radiogroup needs its own accessible name. `htmlFor` can't target a <div>, so the visible
            // title (rendered as a span with this id for multiple_choice) is wired to the group here.
            aria-labelledby={labelText ? `${inputId}-label` : undefined}
            aria-required={isRequired}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : undefined}
          >
            {opts.map((opt) => {
              const optId = `${inputId}-${opt.id}`;
              return (
                <div key={opt.id} className="form-preview-choice-item">
                  <input
                    id={optId}
                    type="radio"
                    name={`fill-mc-${field.id}`}
                    value={opt.value || opt.label}
                    checked={value === (opt.value || opt.label)}
                    onChange={() => onChange(field.id, opt.value || opt.label)}
                  />
                  <label htmlFor={optId}>{opt.label}</label>
                </div>
              );
            })}
          </div>
        );
      }

      default:
        return null;
    }
  };

  // A checkbox carries its own associated <label> (the inline help text, when present), so the title
  // here must NOT be a second <label htmlFor> for the same input — two labels per input is invalid HTML
  // (WCAG 1.3.1). Render the checkbox title as a plain <span>; the input's accessible name comes from
  // its aria-label. All other editable types keep the htmlFor association.
  const labelContent = (
    <>
      {labelText}
      {isRequired && (
        <span className="text-danger ms-1" aria-hidden="true">
          *
        </span>
      )}
    </>
  );

  // The title element differs by control type (flat if/else to avoid nested ternaries):
  //  - checkbox: a plain <span> (the input already owns the inline help <label>; two labels is invalid).
  //  - multiple_choice: a <span> carrying an id, wired to the radiogroup via aria-labelledby — htmlFor
  //    can't bind a label to a role="radiogroup" container, only to a single labelable control.
  //  - everything else: a standard <label htmlFor> bound to the single input.
  let labelNode = null;
  if (labelText) {
    if (field.type === 'checkbox') {
      labelNode = <span className="form-fill-label">{labelContent}</span>;
    } else if (field.type === 'multiple_choice') {
      labelNode = (
        <span className="form-fill-label" id={`${inputId}-label`}>
          {labelContent}
        </span>
      );
    } else {
      labelNode = (
        <label className="form-fill-label" htmlFor={inputId}>
          {labelContent}
        </label>
      );
    }
  }

  return (
    <div className="form-fill-field">
      {labelNode}
      {field.help_text && field.type !== 'checkbox' && (
        <div className="form-preview-help">{resolveTokens(field.help_text, resolved)}</div>
      )}
      {renderInput()}
      {error && (
        <div id={`${inputId}-error`} className="form-fill-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default FillFieldRenderer;
